/**
 * Lesson 5: Limit Orders & The Spread
 *
 * Introduces limit orders, the bid-ask spread,
 * and maker vs taker concepts.
 */

import type { Lesson, SimulationScenario } from '../manager'
import type { Market, OrderBook, Order } from '../../engine/types'

// Helper to create an order
function createOrder(
  id: string,
  side: 'buy' | 'sell',
  price: number,
  quantity: number,
  timestamp: number
): Order {
  return {
    id,
    side,
    type: 'limit',
    price,
    quantity,
    timestamp,
    status: 'open',
    filledQuantity: 0,
  }
}

// Order book with a noticeable spread for limit order practice
const limitOrderBook: OrderBook = {
  bids: [
    createOrder('bid-1', 'buy', 0.58, 60, Date.now() - 5000),
    createOrder('bid-2', 'buy', 0.56, 100, Date.now() - 4000),
    createOrder('bid-3', 'buy', 0.54, 150, Date.now() - 3000),
    createOrder('bid-4', 'buy', 0.52, 80, Date.now() - 2000),
  ],
  asks: [
    createOrder('ask-1', 'sell', 0.64, 50, Date.now() - 5000),
    createOrder('ask-2', 'sell', 0.66, 90, Date.now() - 4000),
    createOrder('ask-3', 'sell', 0.68, 120, Date.now() - 3000),
    createOrder('ask-4', 'sell', 0.70, 70, Date.now() - 2000),
  ],
  trades: [],
  lastPrice: 0.61,
}

const limitOrderMarket: Market = {
  id: 'lesson-5-market',
  question: 'Will the merger be approved?',
  description: 'Practice placing limit orders',
  outcomes: ['Yes', 'No'] as const,
  orderBook: limitOrderBook,
  resolved: false,
  resolution: null,
}

export const limitOrderScenario: SimulationScenario = {
  id: 'limit-order-practice',
  name: 'Limit Order Practice',
  description: 'Learn to set your own price with limit orders',
  market: limitOrderMarket,
  initialOrderBook: limitOrderBook,
  userStartingBalance: 100,
}

export const lesson5: Lesson = {
  id: 'lesson-5',
  title: 'Limit Orders and The Spread',
  description: 'Learn to set your own price and understand maker vs taker',
  order: 5,
  prerequisites: ['lesson-4'],
  completionCriteria: { type: 'interactive-complete' },
  objectives: [
    'Place limit orders at your chosen price',
    'Understand the bid-ask spread',
    'Learn the difference between makers and takers',
  ],
  content: [
    // Page 1: What is a Limit Order?
    {
      type: 'text',
      data: {
        markdown: `# Limit Orders

You've learned market orders. Now let's learn **limit orders**—they give you more control!

## What is a Limit Order?

A **limit order** says: "I want to buy (or sell) at **this specific price or better**."

### Key Differences from Market Orders

| Feature | Market Order | Limit Order |
|---------|--------------|-------------|
| Price | Best available | You choose |
| Execution | Immediate | Only if price is met |
| Guarantee | Will fill | Might not fill |
| Slippage | Possible | None |

### Example

If you place a **limit buy order at $0.60**:
- You'll only pay $0.60 or less
- If no one is selling at $0.60, you wait
- Your order sits on the book until filled or cancelled`,
      },
    },

    // Page 2: How Limit Orders Work
    {
      type: 'text',
      data: {
        markdown: `## How Limit Orders Work

### Limit Buy Order
"Buy at **this price or lower**"
- If asks exist at or below your price → **fills immediately**
- If not → your order **rests on the book** as a new bid

### Limit Sell Order
"Sell at **this price or higher**"
- If bids exist at or above your price → **fills immediately**
- If not → your order **rests on the book** as a new ask

### Example Scenario

Order book state:
- Best Bid: $0.58
- Best Ask: $0.64

**You place: Limit Buy at $0.60**

Since $0.60 < $0.64 (best ask), your order doesn't fill immediately.
Instead, it becomes the **new best bid**!

Now the order book shows:
- Best Bid: **$0.60** ← Your order!
- Best Ask: $0.64`,
        visualizationId: 'order-book',
      },
    },

    // Page 3: The Spread Deep Dive
    {
      type: 'text',
      data: {
        markdown: `## The Bid-Ask Spread

The **spread** is crucial to understanding trading costs.

### Why the Spread Exists

Buyers want to pay **less**, sellers want to receive **more**.
The spread is where they **haven't agreed yet**.

### Trading Within the Spread

In our example (Bid: $0.58, Ask: $0.64, Spread: $0.06):

**Option A: Market order**
- Buy immediately at $0.64 (the ask)
- You pay the "asking price"

**Option B: Limit order at $0.60**
- Your order rests on the book
- You might get filled if someone sells to you
- You save $0.04 per share!

> **Pro tip**: Placing limit orders inside the spread can get you better prices—but you might have to wait.`,
        visualizationId: 'order-book',
      },
    },

    // Page 4: Makers vs Takers
    {
      type: 'text',
      data: {
        markdown: `## Makers vs Takers

These terms describe HOW you trade:

### Makers (Provide Liquidity) 📥
- Place orders that **rest on the book**
- "Make" new liquidity for others
- Usually get better prices
- Example: Limit buy below the ask

### Takers (Remove Liquidity) 📤
- Place orders that **execute immediately**
- "Take" existing liquidity
- Pay the current price
- Example: Market orders, or limit orders that cross the spread

### Why It Matters

| Role | Execution | Price | Waiting |
|------|-----------|-------|---------|
| Maker | Wait for fill | Better | Yes |
| Taker | Immediate | Worse | No |

Many exchanges charge **lower fees** for makers because they add liquidity to the market.

> **Strategy**: If you're not in a hurry, being a maker often gets you better prices.`,
      },
    },

    // Page 5: Interactive practice
    {
      type: 'interactive',
      data: {
        scenario: limitOrderScenario,
        instructions: `## Place a Limit Order

Now let's practice placing a limit order inside the spread.

Look at the current market:
- Best Bid: around $0.58
- Best Ask: around $0.64

### Your Task
1. Select **"Limit"** order type
2. Set price to **60** (that's $0.60)
3. Set quantity to **10**
4. Click **Buy**

Your order should appear in "Open Orders" because it doesn't fill immediately—it's resting on the book waiting for a seller!

Click "Mark as Complete" when done.`,
        hints: [
          'Make sure "Limit" is selected, not "Market".',
          'Enter 60 in the price field (60 cents = $0.60).',
          'Enter 10 in the quantity field.',
          'After clicking Buy, check your "Open Orders" to see it waiting.',
        ],
        successCondition: (state) => {
          // Check if user has any open orders
          return state.orderHistory.some(o => o.status === 'open' || o.status === 'partial')
        },
      },
    },

    // Quiz
    {
      type: 'quiz',
      data: {
        question: 'The best bid is $0.50 and best ask is $0.55. You place a limit buy at $0.52. What happens?',
        options: [
          { id: 'a', text: 'Your order fills immediately at $0.52' },
          { id: 'b', text: 'Your order fills immediately at $0.55' },
          { id: 'c', text: 'Your order rests on the book as the new best bid' },
          { id: 'd', text: 'Your order is rejected' },
        ],
        correctOptionId: 'c',
        explanation:
          'Your limit buy at $0.52 is below the best ask of $0.55, so it won\'t fill immediately. Instead, it rests on the order book and becomes the new best bid (higher than the previous $0.50). You\'re now a "maker" waiting for someone to sell to you.',
      },
    },
  ],
}
